import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  Settings,
  LogOut,
  ChevronDown,
  Mail,
  Phone,
  Calendar,
  Shield,
  Building,
} from "lucide-react";
import UserSettings from "./UserSettings";

const UserProfile = () => {
  const { userProfile, logout, userRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  if (!userProfile) return null;

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Administrateur";
      case "commercial":
        return "Commercial";
      default:
        return "Commercial";
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "admin":
        return "badge badge-info";
      case "commercial":
        return "badge badge-success";
      default:
        return "badge badge-neutral";
    }
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const handleOpenSettings = () => {
    setIsOpen(false);
    setShowSettings(true);
  };

  const formatDate = (timestamp) => {
    try {
      if (timestamp && typeof timestamp.toDate === "function") {
        return timestamp.toDate().toLocaleDateString("fr-FR");
      }
      return "N/A";
    } catch (e) {
      return "N/A";
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-3 px-3 py-2 rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-elevated)] transition-all duration-200"
      >
        <div className="w-10 h-10 bg-gradient-to-br from-brand-600 to-indigo-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-soft">
          {userProfile.firstName?.charAt(0)?.toUpperCase() || "U"}
        </div>

        <div className="text-left hidden sm:block">
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {userProfile.firstName} {userProfile.lastName}
          </p>
          <p className="text-xs text-[var(--color-text-muted)] font-medium">
            {getRoleLabel(userRole)}
          </p>
        </div>

        <ChevronDown
          className={`w-4 h-4 text-[var(--color-text-muted)] transition-all duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 bg-[var(--color-bg-elevated)] rounded-2xl shadow-soft-lg border border-[var(--color-border-soft)] py-2 z-50 animate-fadeIn">
          <div className="px-6 py-4 border-b border-[var(--color-border-soft)]/80">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-brand-600 to-indigo-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-soft">
                {userProfile.firstName?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[var(--color-text-primary)] text-lg">
                  {userProfile.firstName} {userProfile.lastName}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)] font-medium">
                  {userProfile.email}
                </p>
                <span className={`${getRoleBadgeClass(userRole)} mt-2`}>
                  <Shield className="w-3 h-3" />
                  {getRoleLabel(userRole)}
                </span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center gap-4 text-sm text-[var(--color-text-secondary)]">
              <div className="w-8 h-8 bg-[var(--color-brand-soft)] rounded-lg flex items-center justify-center text-[var(--color-brand)]">
                <Mail className="w-4 h-4" />
              </div>
              <span className="font-medium">{userProfile.email}</span>
            </div>

            {userProfile.phoneNumber && (
              <div className="flex items-center gap-4 text-sm text-[var(--color-text-secondary)]">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-emerald-600">
                  <Phone className="w-4 h-4" />
                </div>
                <span className="font-medium">{userProfile.phoneNumber}</span>
              </div>
            )}

            {userProfile.company && (
              <div className="flex items-center gap-4 text-sm text-[var(--color-text-secondary)]">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                  <Building className="w-4 h-4" />
                </div>
                <span className="font-medium">{userProfile.company}</span>
              </div>
            )}

            <div className="flex items-center gap-4 text-sm text-[var(--color-text-secondary)]">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500">
                <Calendar className="w-4 h-4" />
              </div>
              <span className="font-medium">
                Membre depuis {formatDate(userProfile.createdAt)}
              </span>
            </div>

            {userProfile.lastLogin && (
              <div className="flex items-center gap-4 text-sm text-[var(--color-text-secondary)]">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-500">
                  <Calendar className="w-4 h-4" />
                </div>
                <span className="font-medium">
                  Dernière connexion {formatDate(userProfile.lastLogin)}
                </span>
              </div>
            )}
          </div>

          <div className="border-t border-[var(--color-border-soft)]/80 py-2">
            <button
              onClick={handleOpenSettings}
              className="w-full flex items-center gap-4 px-6 py-3 text-sm font-semibold text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-surface)] hover:text-[var(--color-text-primary)] transition-all duration-200"
            >
              <div className="w-8 h-8 bg-[var(--color-bg-surface)] rounded-lg flex items-center justify-center">
                <Settings className="w-4 h-4" />
              </div>
              Paramètres du compte
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-4 px-6 py-3 text-sm font-semibold text-danger hover:bg-danger/10 transition-all duration-200"
            >
              <div className="w-8 h-8 bg-danger/10 rounded-lg flex items-center justify-center text-danger">
                <LogOut className="w-4 h-4" />
              </div>
              Se déconnecter
            </button>
          </div>
        </div>
      )}

      <UserSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
};

export default UserProfile;
